import { useEffect, useState } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";

import { apolloClient } from "@/apollo/client";
import {
  resetAuthenticationState,
  setAuthenticationState,
} from "@/apollo/auth-state";
import { auth } from "@/firebase";
import { GET_DEFAULT_ACCOUNT, type GetDefaultAccountData } from "@/graphql";
import { analytics } from "@/lib/analytics";

export interface FirebaseAuthBootstrapState {
  /** True once the first onAuthStateChanged callback has resolved. */
  ready: boolean;
}

/**
 * Subscribes to firebase auth state, fetches the default Instagram account,
 * and pushes the resulting authentication state into the Apollo reactive var.
 *
 * Replaces the legacy index.tsx onAuthStateChanged → ReactDOM.render flow.
 */
export function useFirebaseAuthBootstrap(): FirebaseAuthBootstrapState {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          await handleSignIn(user);
        } else {
          await handleSignOut();
        }
      } catch (error) {
        console.error("Auth bootstrap failed", error);
      } finally {
        setReady(true);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return { ready };
}

async function handleSignIn(user: User): Promise<void> {
  const { email, displayName, emailVerified, uid } = user;

  analytics.identify(uid, { email, displayName });

  const { data } = await apolloClient.query<GetDefaultAccountData>({
    query: GET_DEFAULT_ACCOUNT,
    fetchPolicy: "network-only",
  });

  const instagrams = data?.instagrams ?? [];
  const accounts = instagrams.map((instagram) => instagram.username);
  const selectedAccount = accounts[0] ?? "";

  setAuthenticationState({
    authenticated: true,
    user: { email, displayName, emailVerified, uid },
    accounts,
    selectedAccount,
  });
}

async function handleSignOut(): Promise<void> {
  resetAuthenticationState();
  analytics.reset();
  await apolloClient.resetStore().catch(() => {
    // resetStore rejects if there are active queries we don't care about here
  });
}
